/**
 * Views Hook
 *
 * Fetches and manages dashboard views via TanStack Query.
 * Default views come from API (defined in code constants).
 * Custom views come from database via API.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { getViews, deleteView as deleteViewApi } from '../api/client';

/** localStorage key for hidden view slugs */
const HIDDEN_VIEWS_KEY = 'danny_hidden_views';

function getHiddenViews(): Set<string> {
  try {
    const stored = localStorage.getItem(HIDDEN_VIEWS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Set(Array.isArray(parsed) ? parsed : []);
    }
  } catch {
    // Ignore parse errors
  }
  return new Set();
}

function setHiddenViews(slugs: Set<string>): void {
  localStorage.setItem(HIDDEN_VIEWS_KEY, JSON.stringify([...slugs]));
}

export function useViews() {
  const [hiddenViews, setHiddenViewsState] = useState<Set<string>>(() => getHiddenViews());
  const queryClient = useQueryClient();

  const {
    data: views = [],
    isLoading,
    error,
    refetch: fetchViews,
  } = useQuery({
    queryKey: ['views'],
    queryFn: getViews,
  });

  const deleteViewMutation = useMutation({
    mutationFn: deleteViewApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['views'] });
    },
  });

  // Listen for changes to hidden views from other components (e.g., Settings)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === HIDDEN_VIEWS_KEY && e.newValue !== null) {
        setHiddenViewsState(getHiddenViews());
      }
    };

    const handleCustomStorageChange = () => {
      const newHiddenViews = getHiddenViews();
      setHiddenViewsState((current) => {
        const currentArray = Array.from(current).sort();
        const newArray = Array.from(newHiddenViews).sort();
        const isDifferent =
          currentArray.length !== newArray.length ||
          currentArray.some((val, idx) => val !== newArray[idx]);
        if (isDifferent) return newHiddenViews;
        return current;
      });
    };

    const handleViewsUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ['views'] });
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('danny-hidden-views-changed', handleCustomStorageChange);
    window.addEventListener('danny-views-updated', handleViewsUpdated);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('danny-hidden-views-changed', handleCustomStorageChange);
      window.removeEventListener('danny-views-updated', handleViewsUpdated);
    };
  }, [queryClient]);

  const handleDeleteView = useCallback(
    async (slug: string): Promise<void> => {
      await deleteViewMutation.mutateAsync(slug);
    },
    [deleteViewMutation]
  );

  const toggleHideView = useCallback((slug: string) => {
    setHiddenViewsState((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      setHiddenViews(next);
      return next;
    });
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('danny-hidden-views-changed'));
    }, 0);
  }, []);

  const isViewHidden = (slug: string) => hiddenViews.has(slug);

  const visibleViews = useMemo(
    () => views.filter((v) => !hiddenViews.has(v.slug)),
    [views, hiddenViews]
  );

  return {
    views,
    visibleViews,
    hiddenViews,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load views') : null,
    refetch: fetchViews,
    deleteView: handleDeleteView,
    toggleHideView,
    isViewHidden,
  };
}
